import React, { useState } from "react";
import type { TalentListItem } from "components/Screens/Marketplace/Customer/types";

import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";

import CustomAvatar from "components/common/Avatar";
import SaveToShortlistButton from "components/Screens/Marketplace/Customer/Shortlist/SaveToShortlistButton";
import { getTalentDisplayName, talentPicture } from "components/Screens/Marketplace/Customer/utils/redaction";

import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CircularProgress from "@mui/material/CircularProgress";

import PakistanFlag from "assets/svgs/PakistanFlag";
import CostaRicaFlag from "assets/svgs/CostaRicaFlag";
import PeruFlag from "assets/svgs/PeruFlag";

import "components/Screens/Marketplace/Customer/common/Withheld/Withheld.scss";
import "./TalentCard.scss";
import experienceLabel from "components/Screens/Marketplace/Customer/utils/experienceLabel";
import ToolChips from "components/Screens/Marketplace/Customer/common/ToolChips/ToolChips";
import speaksLabel from "components/Screens/Marketplace/Customer/utils/speaksLabel";

const FLAG_COMPONENTS: Record<string, React.ComponentType<{ width?: string; height?: string }>> = {
  Pakistan: PakistanFlag,
  "Costa Rica": CostaRicaFlag,
  Peru: PeruFlag,
};

interface TalentCardProps {
  talent: TalentListItem;
  onClick?: () => void;
  // eslint-disable-next-line no-unused-vars
  onRequestInterview?: (_talentId: string) => void;
  isRequesting?: boolean;
  /** PRD-3334: Add to shortlist replaces per-talent Request interview. */
  shortlistMode?: boolean;
  /** The tools to match (the Apps filter on Browse): shown first and outlined in purple. */
  myTools?: string[];
}

function TalentCard({ talent, onClick, onRequestInterview, isRequesting = false, shortlistMode = false, myTools = [] }: TalentCardProps) {
  const { hasPermission } = useTalentApi();
  const FlagComponent = talent.country ? FLAG_COMPONENTS[talent.country] : undefined;
  const fullName = getTalentDisplayName(talent);

  // The card text is the AI recruiter pitch (aiSummary.recruiterPitch) only; no bio. Learn more opens it in place.
  const pitch = talent.recruiterPitch?.trim() || "";
  const [pitchOpen, setPitchOpen] = useState(false);
  const isRequested = talent.interviewRequested || false;

  const canRequestInterview = hasPermission("talentNetwork.request_interview");

  const PITCH_PREVIEW = 175;
  const truncate = (text: string) => (text.length <= PITCH_PREVIEW ? text : `${text.substring(0, PITCH_PREVIEW).trim()}...`);

  const handleRequestInterview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isRequested && !isRequesting && onRequestInterview) {
      // eslint-disable-next-line no-underscore-dangle
      onRequestInterview(talent._id);
    }
  };

  return (
    <div className="talent-card" onClick={onClick} role="button" tabIndex={0}>
      <div className="talent-card__header">
        {/* A withheld photo shows the illustrated avatar for the talent's gender. */}
        <span className="talent-card__avatar-wrap">
          {talentPicture(talent) ? (
            <img src={talentPicture(talent)} alt={fullName} className="talent-card__avatar" />
          ) : (
            <CustomAvatar
              size="xl"
              sx={{ width: 64, height: 64 }}
              src={talent.profilePicture}
              className="talent-card__avatar"
              backgroundColor="var(--color-primitives-neutral-300) !important"
            />
          )}
        </span>

        <div className="talent-card__info">
          <div className="talent-card__name-row">
            <div className="talent-card__name">
              {fullName}
              {FlagComponent && (
                <span className="talent-card__flag">
                  <FlagComponent width="12" height="12" />
                </span>
              )}
            </div>
          </div>

          <div className="talent-card__meta-row">
            <div className="talent-card__meta-item">
              <WorkOutlineOutlinedIcon sx={{ fontSize: 16, color: "#555555" }} />
              <span>{experienceLabel(talent.totalExperience)}</span>
            </div>
            <div className="talent-card__meta-divider" aria-hidden />
            <div className="talent-card__meta-item">
              <TranslateOutlinedIcon sx={{ fontSize: 16, color: "#555555" }} />
              <span>{speaksLabel(talent.languagesSpoken)}</span>
            </div>
          </div>

          {pitch && (
            <div className="talent-card__bio">
              {pitchOpen ? pitch : truncate(pitch)}
              {pitch.length > PITCH_PREVIEW && (
                <button
                  type="button"
                  className="talent-card__learn-more"
                  aria-expanded={pitchOpen}
                  onClick={(e) => {
                    // Expands the pitch in place; the card itself still opens the profile.
                    e.stopPropagation();
                    setPitchOpen((open) => !open);
                  }}
                >
                  {pitchOpen ? "Show less" : "Learn more"}
                </button>
              )}
            </div>
          )}

          <ToolChips tools={talent.tools} myTools={myTools} firstName={talent.firstName} />

          <div className="talent-card__actions">
            <button
              type="button"
              className="talent-card__btn talent-card__btn--primary"
              onClick={(e) => {
                e.stopPropagation();
                if (onClick) onClick();
              }}
            >
              View full profile
            </button>
            {shortlistMode ? (
              // eslint-disable-next-line no-underscore-dangle
              <SaveToShortlistButton talentId={talent._id} interviewRequested={isRequested} />
            ) : (
              canRequestInterview &&
                (isRequested ? (
                  <button
                    type="button"
                    className="talent-card__btn talent-card__btn--disabled"
                    disabled
                    aria-label="Interview requested"
                  >
                    <CheckIcon sx={{ fontSize: 16, color: "#555555" }} />
                    Interview requested
                  </button>
                ) : (
                  <button
                    type="button"
                    className="talent-card__btn talent-card__btn--outlined"
                    onClick={handleRequestInterview}
                    disabled={isRequesting}
                    aria-label="Request interview"
                  >
                  {isRequesting ? (
                    <CircularProgress size={16} sx={{ color: "#7631c2" }} />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="#7631C2" strokeWidth="1.5" />
                      <circle cx="12" cy="9.5" r="3" stroke="#7631C2" strokeWidth="1.5" />
                      <path
                        d="M6.5 19.5C7 16.5 9.2 14.5 12 14.5C14.8 14.5 17 16.5 17.5 19.5"
                        stroke="#7631C2"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                    Request interview
                  </button>
                ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TalentCard;
